import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ImageRecord } from "@workspace/api-client-react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

interface ImageCardProps {
  image: ImageRecord;
  isSelected: boolean;
  onSelect: (id: number, selected: boolean) => void;
  selectionMode: boolean;
}

export function ImageCard({ image, isSelected, onSelect, selectionMode }: ImageCardProps) {
  const isGrouped = image.groupId !== null && image.groupId !== undefined;
  
  const statusConfig = {
    pending: { variant: "secondary" as const, label: "Pending" },
    processing: { variant: "info" as const, label: "Processing" },
    extracted: { variant: "success" as const, label: "Extracted" },
    validated: { variant: "success" as const, label: "Validated" },
    error: { variant: "destructive" as const, label: "Error" },
  };

  const status = statusConfig[image.status] || statusConfig.pending;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -4 }}
      className={cn(
        "photo-card relative group cursor-pointer rounded-xl",
        isGrouped && "ring-2 ring-accent ring-offset-2",
        isSelected && "ring-2 ring-primary ring-offset-2 bg-primary/5"
      )}
      onClick={() => onSelect(image.id, !isSelected)}
    >
      {(selectionMode || isSelected) && (
        <div className="absolute top-3 left-3 z-10">
          <Checkbox 
            checked={isSelected}
            onCheckedChange={(checked) => onSelect(image.id, !!checked)}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "bg-white shadow-sm data-[state=checked]:bg-primary",
              !isSelected && "opacity-0 group-hover:opacity-100"
            )}
          />
        </div>
      )}

      {isGrouped && (
        <div className="absolute top-2 right-2 z-10">
          <Badge variant="warning" className="shadow-sm">
            Pg {image.groupOrder}
          </Badge>
        </div>
      )}

      <div className="aspect-[3/4] w-full bg-muted rounded-lg overflow-hidden relative mb-3 border border-border/50">
        <img
          src={`/api/images/file/${image.filename}`}
          alt={image.originalName}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          onError={(e) => {
            // Fallback for dev mode if proxy isn't serving files correctly
            e.currentTarget.src = "https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=400&h=533&fit=crop";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      <div className="px-1">
        <p className="text-sm font-medium text-foreground truncate" title={image.originalName}>
          {image.originalName}
        </p>
        <div className="mt-2 flex items-center justify-between">
          <Badge variant={status.variant} className="text-[10px] px-2 py-0 h-5">
            {status.label}
          </Badge>
        </div>
      </div>
    </motion.div>
  );
}
